import { apiClient } from '@app/lib'
import { useSugarContext } from '@app/providers'
import { type SugarEnrollmentsResponse } from '@app/types'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useSession } from 'next-auth/react'
import ms from 'ms'

// One request for the whole roster. The status views, their counts and the
// funding-progress denominator are only honest once the client holds every row
// (MIQ-2660 D2), so a partial roster has no use here — getSugarEnrollments walks
// the backend's pages server-side and returns them as a single response.
export const useEnrollments = () => {
  const session = useSession()
  const { rosterProgramYearId: programYearId, siteId } = useSugarContext()

  return useQuery({
    enabled: session.status === 'authenticated' && Boolean(programYearId) && Boolean(siteId),
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<SugarEnrollmentsResponse>(`/api/enrollments`, {
        params: {
          programYearId,
          siteId
        },
        signal
      })

      return data
    },
    // Retained deliberately: the reseed effect in EnrollmentsTable depends on
    // the previous site's rows staying mounted while a new site loads, so the
    // roster does not flash empty and discard the form mid-flight.
    placeholderData: keepPreviousData,
    // The roster is form-bound: a refetch that returns a different set of
    // enrollment ids reseeds the form in EnrollmentsTable. That reseed now
    // carries unsaved edits across, but it still drops the per-row save errors
    // and abandons the settle of a save that is in flight — not worth it for a
    // tab-back or a two-second wifi blip in the middle of a long data-entry
    // session. The roster refreshes on an explicit action instead: the Retry
    // button, a site or program-year switch, or a save.
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    queryKey: ['enrollments', { programYearId, siteId }],
    staleTime: ms('5m')
  })
}
